import { AUTH_REQUIRED_EVENT } from '../auth/constants';
import { BASE_URL, REQUIRE_EDITOR_LOGIN } from '../config';
import {
  getAuthToken,
  getAuthTokenExpiresAt,
  setAuthSession,
  clearAuthToken,
} from '../auth/token';

class ApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const broadcastAuthRequired = (detail) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }
  try {
    window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT, { detail }));
  } catch {
    // no-op
  }
};

const parseResponseBody = async (response) => {
  const contentType = response.headers?.get?.('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const REFRESH_SKEW_MS = 5 * 60 * 1000;
let refreshPromise = null;

const isAuthEndpoint = (endpoint) => {
  const text = String(endpoint || '');
  return text.includes('/auth/login') || text.includes('/auth/refresh');
};

const shouldRefreshBeforeRequest = () => {
  const token = getAuthToken();
  const expiresAt = getAuthTokenExpiresAt();
  if (!token || !expiresAt) return false;
  const expiresMs = Date.parse(expiresAt);
  return Number.isFinite(expiresMs) && expiresMs - Date.now() <= REFRESH_SKEW_MS;
};

const refreshAuthToken = async () => {
  const token = getAuthToken();
  if (!token) return null;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await window.fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'x-1line-key': '1',
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await parseResponseBody(response).catch(() => null);
      if (!response.ok || !payload?.token) {
        throw new ApiError(
          payload?.message || payload?.error || response.statusText || 'Authentication required.',
          { status: response.status, data: payload }
        );
      }
      setAuthSession(payload);
      return payload.token;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  const refreshed = await refreshPromise;
  return refreshed;
};

// A tiny wrapper around fetch()
export async function client(endpoint, { body, _retryAuth = true, ...customConfig } = {}) {
  if (REQUIRE_EDITOR_LOGIN && !isAuthEndpoint(endpoint) && shouldRefreshBeforeRequest()) {
    await refreshAuthToken().catch(() => null);
  }

  const headers = {
    'x-1line-key': '1', // Use string for consistency
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  const authToken = getAuthToken();
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const config = {
    method: body ? 'POST' : 'GET', // Default to POST if body is present
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers, // Allow overriding headers
    },
  };

  if (body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  if (body && !(body instanceof FormData)) {
    config.body = JSON.stringify(body);
  } else if (body) {
    config.body = body;
  }

  try {
    const response = await window.fetch(endpoint, config);
    const payload = await parseResponseBody(response).catch(() => null);

    if (!response.ok) {
      if (response.status === 401 && REQUIRE_EDITOR_LOGIN) {
        if (_retryAuth && !isAuthEndpoint(endpoint)) {
          try {
            await refreshAuthToken();
            return client(endpoint, { body, ...customConfig, _retryAuth: false });
          } catch {
            // Fall through to auth-required handling.
          }
        }
        clearAuthToken();
        broadcastAuthRequired({
          endpoint,
          status: response.status,
          message: payload?.message || response.statusText,
        });
      }

      const errorMessage =
        payload?.message ||
        payload?.error ||
        response.statusText ||
        'An error occurred';

      throw new ApiError(errorMessage, { status: response.status, data: payload });
    }

    return payload;
  } catch (err) {
    if (err instanceof ApiError) {
      return Promise.reject(err);
    }
    const fallback = err instanceof Error ? err.message : 'An unexpected error occurred';
    return Promise.reject(new ApiError(fallback));
  }
}

// Convenience methods for common HTTP verbs
client.get = function (endpoint, customConfig = {}) {
  return client(endpoint, { ...customConfig, method: 'GET' });
};

client.post = function (endpoint, body, customConfig = {}) {
  return client(endpoint, { ...customConfig, body, method: 'POST' });
};

client.put = function (endpoint, body, customConfig = {}) {
  return client(endpoint, { ...customConfig, body, method: 'PUT' });
};

client.delete = function (endpoint, customConfig = {}) {
  return client(endpoint, { ...customConfig, method: 'DELETE' });
};
